import {
  TextField,
  Button,
  Typography,
  Box,
  // Divider,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
} from "@mui/material";
// import GoogleIcon from "@mui/icons-material/Google";
// import FacebookIcon from "@mui/icons-material/Facebook";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useEffect, useState } from "react";
import {
  verifyCharityRegistration,
  setCharityNotVerified,
} from "../redux/authSlice";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";

interface RegistrationFormProps {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  // ssn: string;
  // dateOfBirth: Date | null;
  password: string;
  confirmPassword: string;
  error?: string;
  onUsernameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  // onSsnChange: (value: string) => void;
  // onDateOfBirthChange: (value: Date | null) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onSubmit: (isCharity: boolean, ein?: string, orgName?: string) => void;
  onGoogleSignUp: () => void;
  onFacebookSignUp: () => void;
  onBackToLogin?: () => void;
  isSubmitting: boolean;
}

export const RegistrationForm = ({
  username,
  email,
  firstName,
  lastName,
  phone,
  // dateOfBirth,
  // ssn,
  password,
  confirmPassword,
  error,
  onUsernameChange,
  onEmailChange,
  onFirstNameChange,
  onLastNameChange,
  onPhoneChange,
  // onDateOfBirthChange,
  // onSsnChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onGoogleSignUp,
  onFacebookSignUp,
  isSubmitting,
  onBackToLogin,
}: RegistrationFormProps) => {
  const [isCharity, setIsCharity] = useState(false);
  const [ein, setEin] = useState("");
  const [orgName, setOrgName] = useState("");

  const [einError, setEinError] = useState("");
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const dispatch = useAppDispatch();
  const {
    loading: isVerifying,
    charityVerified: einVerified,
    verificationError,
    charityName: orgNameFromState,
  } = useAppSelector((state) => state.auth);

  const { chainType } = useAppSelector((state) => state.wallet);

  const handleVerifyEin = async () => {
    if (!ein) {
      setEinError("EIN is required.");
      return;
    }
    dispatch(verifyCharityRegistration({ charityId: ein }));
    setEinError("");
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    // Username validation
    // if (!username.trim()) {
    //   errors.username = "Username is required";
    // }

    // Email validation
    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address";
    }

    // Name validation
    if (!isCharity) {
      if (!firstName.trim()) {
        errors.firstName = "First name is required";
      }
      if (!lastName.trim()) {
        errors.lastName = "Last name is required";
      }
    }

    // Phone validation
    if (!phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (
      !/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(phone)
    ) {
      errors.phone = "Please enter a valid phone number";
    }

    // Date of birth validation
    // if (!isCharity && dateOfBirth) {
    //   const today = new Date();
    //   const birthDate = new Date(dateOfBirth);
    //   let age = today.getFullYear() - birthDate.getFullYear();
    //   const monthDiff = today.getMonth() - birthDate.getMonth();

    //   if (
    //     monthDiff < 0 ||
    //     (monthDiff === 0 && today.getDate() < birthDate.getDate())
    //   ) {
    //     age--;
    //   }

    //   if (age < 18) {
    //     errors.dateOfBirth = "You must be at least 18 years old";
    //   }
    // }

    // Password validation
    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    if (isCharity) {
      if (!einVerified) {
        setEinError("Please verify your EIN first");
        return;
      }
      if (!orgName) {
        return;
      }
      onSubmit(true, ein, orgName);
    } else {
      onSubmit(false);
    }
  };

  useEffect(() => {
    if (orgNameFromState) {
      setOrgName(orgNameFromState);
    }
  }, [orgNameFromState]);

  const scrollableBoxStyles = {
    maxHeight: "80vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  };

  return (
    <Box sx={scrollableBoxStyles}>
      <Stack spacing={2}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Register as:</FormLabel>
          <RadioGroup
            row
            value={isCharity ? "charity" : "user"}
            onChange={(e) => setIsCharity(e.target.value === "charity")}
          >
            <FormControlLabel
              value="user"
              control={<Radio />}
              label="Individual"
            />
            <FormControlLabel
              value="charity"
              control={<Radio />}
              label="Charity/Organization"
            />
          </RadioGroup>
        </FormControl>

        {isCharity && (
          <>
            <TextField
              label="Organization Name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              fullWidth
              required
              error={!orgName && validationErrors.orgName !== undefined}
              helperText={!orgName ? "Organization name is required" : ""}
            />
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                label="EIN (Tax ID)"
                value={ein}
                onChange={(e) => {
                  setEin(e.target.value);
                  dispatch(setCharityNotVerified());
                }}
                fullWidth
                required
                error={!!einError}
                helperText={einError}
              />
              <Button
                variant="contained"
                onClick={handleVerifyEin}
                disabled={isVerifying || einVerified}
                sx={{ height: "56px" }}
              >
                {isVerifying ? (
                  <CircularProgress size={24} />
                ) : einVerified ? (
                  "Verified"
                ) : (
                  "Verify"
                )}
              </Button>
            </Box>
            {einVerified && (
              <Typography color="success.main" variant="body2">
                EIN verified successfully
              </Typography>
            )}
            {verificationError && (
              <Typography color="error.main" variant="body2">
                {verificationError}
              </Typography>
            )}
          </>
        )}

        {/* <TextField
          label={chainType === "solana" ? "Believe Tag" : "Username"}
          value={username}
          onChange={(e) => onUsernameChange(e.target.value)}
          fullWidth
          required
          error={!!validationErrors.username}
          helperText={validationErrors.username}
        /> */}
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          fullWidth
          required
          error={!!validationErrors.email}
          helperText={validationErrors.email}
        />

        {!isCharity && (
          <>
            <Box display="flex" gap={2}>
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => onFirstNameChange(e.target.value)}
                fullWidth
                required
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
              />
              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => onLastNameChange(e.target.value)}
                fullWidth
                required
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
              />
            </Box>
            {/* <DatePicker
              label="Date of Birth"
              value={dateOfBirth}
              onChange={onDateOfBirthChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !!validationErrors.dateOfBirth,
                  helperText: validationErrors.dateOfBirth,
                },
              }}
            /> */}
          </>
        )}
        {/*chainType === "solana" && (
          <TextField
            label="Ssn"
            value={ssn}
            onChange={(e) => onSsnChange(e.target.value)}
            fullWidth
            required
          />
        )*/}

        <TextField
          label="Phone Number"
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          fullWidth
          required
          error={!!validationErrors.phone}
          helperText={validationErrors.phone}
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          fullWidth
          required
          error={!!validationErrors.password}
          helperText={validationErrors.password}
        />
        <TextField
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => onConfirmPasswordChange(e.target.value)}
          fullWidth
          required
          error={!!validationErrors.confirmPassword}
          helperText={validationErrors.confirmPassword}
        />

        {error && <Typography color="error">{error}</Typography>}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || (isCharity && !einVerified)}
          fullWidth
          size="large"
        >
          {isSubmitting
            ? "Creating account..."
            : isCharity
            ? "Register Organization"
            : "Register"}
        </Button>

        <Button
          variant="contained"
          onClick={onBackToLogin}
          disabled={isSubmitting}
          fullWidth
          size="large"
        >
          Back to Login
        </Button>

        {/* <Divider>OR</Divider>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={onGoogleSignUp}
            fullWidth
          >
            Google
          </Button>
          <Button
            variant="outlined"
            startIcon={<FacebookIcon />}
            onClick={onFacebookSignUp}
            fullWidth
          >
            Facebook
          </Button>
        </Stack> */}
      </Stack>
    </Box>
  );
};
